import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import {
  addCustomerDesignItems,
  CUSTOMER_DESIGN_CART_ERROR_CODES,
  prepareCustomerDesignAdd,
} from '@theme/customer-design-cart';
import { formatMoney } from '@theme/money-formatting';
import { CUSTOMER_DESIGN_EVENT, getResolvedSelection } from '@theme/customer-design-state';
import {
  logStorefrontError,
  logStorefrontEvent,
  responseDiagnosticDetails,
} from '@theme/storefront-diagnostics';

const FINAL_DESIGN_STATUSES = new Set(['approved', 'order_complete', 'final']);

class VariantQuantityGrid extends HTMLElement {
  /** @type {AbortController} */
  #lifecycleController = new AbortController();

  /** @type {AbortController | null} */
  #requestController = null;

  /** @type {Element | null} */
  #productEventTarget = null;

  /** @type {number | undefined} */
  #stateTimer;

  #isSubmitting = false;

  /** @type {(() => void) | null} */
  #pendingProductContentMorph = null;

  #designCompletion = null;

  /** @type {ReturnType<typeof getResolvedSelection>} */
  #selectedDesign = null;

  connectedCallback() {
    this.#lifecycleController = new AbortController();
    const { signal } = this.#lifecycleController;

    this.addEventListener('click', this.#handleQuantityButtonClick, { signal });
    this.addEventListener('input', this.#handleQuantityInput, { signal });
    this.addEventListener('change', this.#handleQuantityChange, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });

    this.#productEventTarget = this.closest('.shopify-section, dialog');
    this.#productEventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(CUSTOMER_DESIGN_EVENT, this.#handleCustomerDesignChange, { signal });

    this.#hydrateSelectedDesign();
  }

  disconnectedCallback() {
    this.#lifecycleController.abort();
    this.#requestController?.abort();
    this.#settleDesignCompletion(undefined, new DOMException('The operation was aborted.', 'AbortError'));
    if (this.#stateTimer) window.clearTimeout(this.#stateTimer);
    this.#pendingProductContentMorph = null;
    this.#productEventTarget = null;
  }

  hasSelectedQuantity() {
    return this.#quantityInputs.some((input) => {
      return !input.disabled && (Number.parseInt(input.value, 10) || 0) > 0;
    });
  }

  completeDesignSelection() {
    this.#hydrateSelectedDesign();

    if (!this.#isCustomizable || !this.#hasSelectedDesign || !this.hasSelectedQuantity()) {
      return Promise.reject(new Error(this.dataset.errorLabel || 'customer_design_add_unavailable'));
    }

    const form = this.querySelector('[data-quantity-grid-form]');
    if (!(form instanceof HTMLFormElement)) {
      return Promise.reject(new Error(this.dataset.errorLabel || 'customer_design_add_unavailable'));
    }

    if (this.#designCompletion) return this.#designCompletion.promise;
    if (this.#isSubmitting) {
      return Promise.reject(new Error(this.dataset.errorLabel || 'customer_design_add_in_progress'));
    }
    // requestSubmit never fires the submit handler when native validation fails, which would
    // leave the completion promise (and the design popup awaiting it) pending forever.
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity?.();
      return Promise.reject(new Error(this.dataset.errorLabel || 'customer_design_add_unavailable'));
    }

    let resolve;
    let reject;
    const promise = new Promise((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    this.#designCompletion = { promise, resolve, reject };

    const submitButton = this.#submitButton;
    if (submitButton) {
      form.requestSubmit(submitButton);
    } else {
      form.requestSubmit();
    }
    return promise;
  }

  #settleDesignCompletion(value, error = null) {
    const completion = this.#designCompletion;
    if (!completion) return;
    this.#designCompletion = null;
    if (error) completion.reject(error);
    else completion.resolve(value);
  }

  #customerErrorMessage(error) {
    if (
      error?.code === 'deposit_unit_price_too_low' ||
      error?.customerMessageKey === 'customer_designs.deposit.price_too_low'
    ) {
      return this.dataset.depositPriceError || this.dataset.errorLabel || '';
    }

    if (error?.code === CUSTOMER_DESIGN_CART_ERROR_CODES.ROLLBACK_FAILED) {
      return this.dataset.designCartRecoveryError || this.dataset.designCartError || this.dataset.errorLabel || '';
    }

    if (error?.code === CUSTOMER_DESIGN_CART_ERROR_CODES.DESIGN_CONFLICT) {
      return this.dataset.designCartConflictError || this.dataset.designCartError || this.dataset.errorLabel || '';
    }

    if (Object.values(CUSTOMER_DESIGN_CART_ERROR_CODES).includes(error?.code)) {
      return this.dataset.designCartError || this.dataset.errorLabel || '';
    }

    return this.dataset.errorLabel || '';
  }

  /** @returns {HTMLInputElement[]} */
  get #quantityInputs() {
    return Array.from(this.querySelectorAll('[data-variant-quantity]')).filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  /** @returns {HTMLButtonElement | null} */
  get #submitButton() {
    const button = this.querySelector('[data-quantity-grid-submit]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @returns {HTMLElement | null} */
  get #submitLabel() {
    const label = this.querySelector('[data-quantity-grid-submit-label]');
    return label instanceof HTMLElement ? label : null;
  }

  /** @returns {HTMLElement | null} */
  get #submitPrices() {
    const prices = this.querySelector('[data-quantity-grid-submit-prices]');
    return prices instanceof HTMLElement ? prices : null;
  }

  /** @returns {HTMLElement | null} */
  get #regularTotal() {
    const total = this.querySelector('[data-quantity-grid-regular-total]');
    return total instanceof HTMLElement ? total : null;
  }

  /** @returns {HTMLElement | null} */
  get #discountedTotal() {
    const total = this.querySelector('[data-quantity-grid-discounted-total]');
    return total instanceof HTMLElement ? total : null;
  }

  /** @returns {HTMLElement | null} */
  get #status() {
    const status = this.querySelector('[data-quantity-grid-status]');
    return status instanceof HTMLElement ? status : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get #designBadge() {
    const badge = this.querySelector('[data-customer-design-badge]');
    return badge instanceof HTMLButtonElement ? badge : null;
  }

  /** @returns {HTMLElement | null} */
  get #designBadgeName() {
    const name = this.querySelector('[data-customer-design-badge-name]');
    return name instanceof HTMLElement ? name : null;
  }

  /** @returns {HTMLImageElement | null} */
  get #designBadgeImage() {
    const image = this.querySelector('[data-customer-design-badge-image]');
    return image instanceof HTMLImageElement ? image : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get #newDesignButton() {
    const button = this.querySelector('[data-customer-design-new]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  get #isCustomizable() {
    return this.dataset.customizable === 'true';
  }

  get #hasSelectedDesign() {
    const selection = this.#selectedDesign;

    return Boolean(
      selection?.handle &&
        selection.id &&
        selection.kitName &&
        (selection.designId || selection.designHandle) &&
        (selection.customerOwned || selection.claimToken)
    );
  }

  get #needsDesign() {
    return this.#isCustomizable && !this.#hasSelectedDesign;
  }

  get #showCustomizeCta() {
    if (!this.#isCustomizable || this.#isSubmitting) return false;
    if (this.dataset.state === 'loading' || this.dataset.state === 'success') return false;
    if (this.#hasSelectedDesign) return false;

    return !this.hasSelectedQuantity();
  }

  get #customerDesignStatus() {
    const status = this.#selectedDesign?.status || '';
    return FINAL_DESIGN_STATUSES.has(status) ? 'final' : 'draft';
  }

  #hydrateSelectedDesign() {
    this.#selectedDesign = this.#isCustomizable ? getResolvedSelection() : null;
    const hasSelectedDesign = this.#hasSelectedDesign;
    this.dataset.designSelected = hasSelectedDesign.toString();

    const designBadge = this.#designBadge;
    if (designBadge) {
      designBadge.hidden = !hasSelectedDesign;
      designBadge.dataset.customerDesignProof = this.#customerDesignStatus;

      if (hasSelectedDesign) {
        designBadge.setAttribute(
          'aria-label',
          `${this.dataset.yourDesignLabel || ''}: ${this.#selectedDesign?.kitName || ''}`
        );
      } else {
        designBadge.removeAttribute('aria-label');
      }
    }

    const newDesignButton = this.#newDesignButton;
    if (newDesignButton) newDesignButton.hidden = !hasSelectedDesign;

    if (this.#designBadgeName) {
      this.#designBadgeName.textContent = hasSelectedDesign ? this.#selectedDesign?.kitName || '' : '';
    }

    const designBadgeImage = this.#designBadgeImage;
    if (designBadgeImage) {
      const imageUrl = hasSelectedDesign ? this.#selectedDesign?.imageUrl || '' : '';

      if (imageUrl) {
        designBadgeImage.src = imageUrl;
        designBadgeImage.alt = this.#selectedDesign?.imageAlt || '';
        designBadgeImage.hidden = false;
      } else {
        designBadgeImage.removeAttribute('src');
        designBadgeImage.alt = '';
        designBadgeImage.hidden = true;
      }
    }

    this.#setState('idle');
  }

  #handleCustomerDesignChange = () => {
    this.#hydrateSelectedDesign();
  };

  /**
   * @param {Event} event
   * @param {{addToCartOnSave?: boolean}} [options]
   */
  #openCustomerDesignBuilder(event, { addToCartOnSave = true } = {}) {
    const builder = document.getElementById('customer-design-builder');
    if (!(builder instanceof HTMLElement) || !('open' in builder) || typeof builder.open !== 'function') return;

    const dialog = builder.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) return;

    builder.open({ source: 'pdp', gridId: this.id, addToCartOnSave }, event);
  }

  /** @returns {Record<string, string> | null} */
  get #customerDesignProperties() {
    if (!this.#isCustomizable || !this.#hasSelectedDesign || !this.#selectedDesign) return null;

    return {
      Design: this.#selectedDesign.kitName,
      _customer_design_handle: this.#selectedDesign.handle,
      _customer_design_id: this.#selectedDesign.id,
      _design_status: this.#customerDesignStatus,
    };
  }

  #syncSubmitLabel() {
    if (!this.#submitLabel) return;

    if (this.#showCustomizeCta) {
      this.#submitLabel.textContent = this.dataset.customizeShortLabel || this.dataset.customizeLabel || '';
    } else if (this.#needsDesign) {
      this.#submitLabel.textContent = this.dataset.customizeLabel || '';
    } else if (this.dataset.state === 'loading') {
      this.#submitLabel.textContent = this.dataset.addingLabel || this.dataset.addLabel || '';
    } else if (this.dataset.state === 'success') {
      this.#submitLabel.textContent = this.dataset.addedLabel || this.dataset.addLabel || '';
    } else if (this.#isCustomizable && this.#hasSelectedDesign) {
      this.#submitLabel.textContent = this.dataset.addToKitLabel || this.dataset.addLabel || '';
    } else {
      this.#submitLabel.textContent = this.dataset.addLabel || '';
    }
  }

  /** @returns {Array<{minimumQuantity: number, percentage: number}>} */
  get #quantityDiscountTiers() {
    try {
      const tiers = JSON.parse(this.dataset.quantityDiscountTiers || '[]');
      if (!Array.isArray(tiers)) return [];

      return tiers
        .map((tier) => ({
          minimumQuantity: Number(tier?.minimumQuantity),
          percentage: Number(tier?.percentage),
        }))
        .filter((tier) => {
          return (
            Number.isInteger(tier.minimumQuantity) &&
            tier.minimumQuantity > 0 &&
            Number.isFinite(tier.percentage) &&
            tier.percentage > 0 &&
            tier.percentage <= 100
          );
        })
        .sort((a, b) => a.minimumQuantity - b.minimumQuantity);
    } catch (error) {
      logStorefrontError('add-to-cart', 'Quantity discount tiers could not be parsed.', error, {
        operation: 'parse-quantity-discount-tiers',
        productId: this.dataset.productId || '',
        sourceId: this.dataset.blockId || '',
      });
      return [];
    }
  }

  #syncPricing() {
    const submitPrices = this.#submitPrices;
    const regularTotalElement = this.#regularTotal;
    const discountedTotalElement = this.#discountedTotal;
    if (!submitPrices || !regularTotalElement || !discountedTotalElement) return;

    let selectedQuantity = 0;
    let regularTotal = 0;

    for (const input of this.#quantityInputs) {
      if (input.disabled) continue;

      const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
      const price = Math.max(0, Number.parseInt(input.dataset.price || '0', 10) || 0);
      selectedQuantity += quantity;
      regularTotal += quantity * price;
    }

    if (selectedQuantity === 0 || regularTotal === 0) {
      submitPrices.hidden = true;
      this.#syncPriceLadder(0, null);
      return;
    }

    const qualifyingQuantity = selectedQuantity + (Number.parseInt(this.dataset.cartProductQuantity || '0', 10) || 0);
    const earnedTier = this.#quantityDiscountTiers.reduce((currentTier, tier) => {
      return qualifyingQuantity >= tier.minimumQuantity ? tier : currentTier;
    }, null);

    this.#syncPriceLadder(qualifyingQuantity, earnedTier);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    regularTotalElement.textContent = formatMoney(regularTotal, format, currency);
    regularTotalElement.classList.toggle('variant-quantity-grid__submit-price--compare', Boolean(earnedTier));

    if (earnedTier) {
      const discountedTotal = Math.round(regularTotal * (1 - earnedTier.percentage / 100));
      discountedTotalElement.textContent = formatMoney(discountedTotal, format, currency);
      discountedTotalElement.hidden = false;
    } else {
      discountedTotalElement.textContent = '';
      discountedTotalElement.hidden = true;
    }

    submitPrices.hidden = false;
  }

  /**
   * Lights the price-ladder step the customer has earned.
   *
   * @param {number} qualifyingQuantity
   * @param {{minimumQuantity: number, percentage: number} | null} earnedTier
   */
  #syncPriceLadder(qualifyingQuantity, earnedTier) {
    const rungs = this.querySelectorAll('[data-quantity-grid-rung]');
    if (rungs.length === 0) return;

    const earnedMinimum = earnedTier ? earnedTier.minimumQuantity : 1;

    for (const rung of rungs) {
      const minimum = Number.parseInt(rung.dataset.minimumQuantity || '0', 10) || 0;
      const isEarned = qualifyingQuantity > 0 && minimum === earnedMinimum;

      rung.classList.toggle('variant-quantity-grid__rung--earned', isEarned);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {{minimum: number, increment: number, maximum: number}}
   */
  #getConstraints(input) {
    const minimum = Math.max(1, Number.parseInt(input.dataset.minimum || '1', 10) || 1);
    const increment = Math.max(1, Number.parseInt(input.dataset.increment || '1', 10) || 1);
    const maximum = input.max === '' ? Number.POSITIVE_INFINITY : Number.parseInt(input.max, 10);

    return {
      minimum,
      increment,
      maximum: Number.isNaN(maximum) ? Number.POSITIVE_INFINITY : maximum,
    };
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {number}
   */
  #normalizeQuantity(input) {
    const { minimum, increment, maximum } = this.#getConstraints(input);
    const parsedValue = Number.parseInt(input.value, 10);

    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      input.value = '0';
      return 0;
    }

    const maximumValidQuantity = Number.isFinite(maximum)
      ? minimum + Math.floor((maximum - minimum) / increment) * increment
      : Number.POSITIVE_INFINITY;
    const steppedValue = minimum + Math.round((Math.max(parsedValue, minimum) - minimum) / increment) * increment;
    const quantity = Math.min(steppedValue, maximumValidQuantity);

    input.value = Math.max(0, quantity).toString();
    return Math.max(0, quantity);
  }

  /** @param {HTMLInputElement} input */
  #syncInputButtons(input) {
    const row = input.closest('[data-variant-row]');
    if (!row) return;

    const decreaseButton = row.querySelector('[data-quantity-action="decrease"]');
    const increaseButton = row.querySelector('[data-quantity-action="increase"]');
    const { minimum, increment, maximum } = this.#getConstraints(input);
    const quantity = Number.parseInt(input.value, 10) || 0;
    const unavailable = input.disabled || row.getAttribute('data-available') !== 'true';

    if (decreaseButton instanceof HTMLButtonElement) {
      decreaseButton.disabled = unavailable || quantity <= 0;
    }

    if (increaseButton instanceof HTMLButtonElement) {
      const nextQuantity = quantity <= 0 ? minimum : quantity + increment;
      increaseButton.disabled = unavailable || nextQuantity > maximum;
    }
  }

  #syncControls() {
    const needsDesign = this.#needsDesign;
    let hasQuantity = false;
    let hasQuantityControls = false;

    for (const input of this.#quantityInputs) {
      input.setCustomValidity('');
      this.#syncInputButtons(input);
      if (!input.disabled) hasQuantityControls = true;
      const rowQuantity = input.disabled ? 0 : Number.parseInt(input.value, 10) || 0;
      if (rowQuantity > 0) hasQuantity = true;
      input
        .closest('[data-variant-row]')
        ?.classList.toggle('variant-quantity-grid__row--has-qty', rowQuantity > 0);
    }

    const showCustomizeCta = this.#showCustomizeCta;
    const submitButton = this.#submitButton;
    if (submitButton) {
      const submitDisabled = this.#isSubmitting || (!hasQuantity && !showCustomizeCta);
      // Missing quantities are the shopper's next step rather than a dead end, so the
      // button stays enabled and says what to do instead of greying out.
      const awaitingQuantity = submitDisabled && !this.#isSubmitting && hasQuantityControls;
      submitButton.disabled = submitDisabled && !awaitingQuantity;

      if (awaitingQuantity) {
        submitButton.dataset.awaitingQuantity = '';
        submitButton.title = this.dataset.selectQuantityHint || '';
      } else {
        delete submitButton.dataset.awaitingQuantity;
        submitButton.removeAttribute('title');
      }

      if (submitDisabled && !awaitingQuantity) {
        submitButton.setAttribute('aria-disabled', 'true');
      } else {
        submitButton.removeAttribute('aria-disabled');
      }

      if (needsDesign || showCustomizeCta) {
        submitButton.setAttribute('aria-haspopup', 'dialog');
      } else {
        submitButton.removeAttribute('aria-haspopup');
      }
    }

    const designBadge = this.#designBadge;
    if (designBadge) {
      designBadge.disabled = false;
      designBadge.removeAttribute('aria-disabled');
    }

    const newDesignButton = this.#newDesignButton;
    if (newDesignButton) {
      newDesignButton.disabled = false;
      newDesignButton.removeAttribute('aria-disabled');
    }

    this.#syncSubmitLabel();
    this.#syncPricing();
  }

  /** @param {Array<{variant_id?: number | string, product_id?: number | string, quantity?: number}>} items */
  #syncCartConstraints(items) {
    for (const input of this.#quantityInputs) {
      const variantId = input.dataset.variantId || '';
      const cartQuantity = items.reduce((quantity, item) => {
        return String(item.variant_id) === variantId ? quantity + (Number(item.quantity) || 0) : quantity;
      }, 0);
      const maximum = Number.parseInt(input.dataset.maximum || '', 10);

      input.dataset.cartQuantity = cartQuantity.toString();

      if (Number.isFinite(maximum)) {
        input.max = Math.max(0, maximum - cartQuantity).toString();
        this.#normalizeQuantity(input);
      }
    }

    const cartProductQuantity = items.reduce((quantity, item) => {
      return String(item.product_id) === this.dataset.productId ? quantity + (Number(item.quantity) || 0) : quantity;
    }, 0);
    this.dataset.cartProductQuantity = cartProductQuantity.toString();

    this.#syncControls();
  }

  /** @param {Event} event */
  #handleQuantityButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-quantity-action]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const row = button.closest('[data-variant-row]');
    const input = row?.querySelector('[data-variant-quantity]');
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    event.preventDefault();
    const { minimum, increment, maximum } = this.#getConstraints(input);
    const currentQuantity = this.#normalizeQuantity(input);
    let nextQuantity = currentQuantity;

    if (button.dataset.quantityAction === 'increase') {
      nextQuantity = currentQuantity <= 0 ? minimum : currentQuantity + increment;
      nextQuantity = Math.min(nextQuantity, maximum);
    } else {
      nextQuantity = currentQuantity <= minimum ? 0 : Math.max(minimum, currentQuantity - increment);
    }

    input.value = nextQuantity.toString();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {Event} event */
  #handleQuantityInput = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-variant-quantity]')) return;
    this.#syncControls();
  };

  /** @param {Event} event */
  #handleQuantityChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-variant-quantity]')) return;
    this.#normalizeQuantity(event.target);
    this.#syncControls();
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (Array.isArray(detail?.items)) this.#syncCartConstraints(detail.items);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          logStorefrontError('add-to-cart', 'Cart update event failed.', error, {
            operation: 'sync-variant-grid',
            productId: this.dataset.productId,
            sourceId: this.dataset.blockId,
          });
        }
      });
  };

  /**
   * @param {'idle' | 'loading' | 'success' | 'error'} state
   * @param {string} [message]
   */
  #setState(state, message = '') {
    this.dataset.state = state;
    this.setAttribute('aria-busy', (state === 'loading').toString());

    if (this.#status) this.#status.textContent = message;

    this.#syncControls();
  }

  /** @returns {string[]} */
  #getCartSectionIds() {
    const sectionIds = new Set();

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }

    return Array.from(sectionIds);
  }

  async #fetchCart() {
    const endpoint = `${Theme.routes.cart_url}.json`;
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: this.#requestController?.signal,
    });

    if (!response.ok) {
      const error = new Error(this.dataset.errorLabel || 'cart_fetch_failed');
      logStorefrontError('cart', 'Cart fetch failed.', error, {
        operation: 'fetch-cart',
        productId: this.dataset.productId,
        sourceId: this.dataset.blockId,
        ...responseDiagnosticDetails(response, null, endpoint),
      });
      throw error;
    }
    return response.json();
  }

  /**
   * Keep the Horizon cart-add path native for every product. Custom designs
   * are complete line item property snapshots, so Liquid can render the cart
   * directly after the normal section refresh.
   *
   * @param {Array<{id: string, quantity: number}>} items
   * @param {number} totalQuantity
   */
  async #addStandardItems(items, totalQuantity, properties = null) {
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const endpoint = Theme.routes.cart_add_url;
    let requestResponse;
    let responsePayload;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    logStorefrontEvent('add-to-cart', 'Variant-grid add request started.', {
      operation: properties ? 'add-design-lines' : 'add-standard-lines',
      endpoint,
      productId: this.dataset.productId,
      sourceId: this.dataset.blockId,
      lineCount: items.length,
      requestedQuantity: totalQuantity,
      variantIds: items.map((item) => item.id),
    });

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.id),
            quantity: item.quantity,
            ...(item.properties || properties ? { properties: item.properties || properties } : {}),
            ...(item.sellingPlanId ? { selling_plan: Number(item.sellingPlanId) } : {}),
          })),
          sections: this.#getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
        signal: this.#requestController?.signal,
      });
      requestResponse = response;
      responsePayload = await response.json();

      if (!response.ok || responsePayload.status) {
        const errorMessage = responsePayload.description || responsePayload.message || this.dataset.errorLabel || '';
        throw Object.assign(new Error(errorMessage), { payload: responsePayload });
      }

      const ajaxCart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: false,
          items: ajaxCart.items,
          sections: responsePayload.sections,
          source: 'variant-quantity-grid',
          sourceId: this.dataset.blockId || '',
          itemCount: totalQuantity,
          productId: this.dataset.productId || '',
        },
      });

      logStorefrontEvent('add-to-cart', 'Variant-grid add request completed.', {
        operation: properties ? 'add-design-lines' : 'add-standard-lines',
        endpoint,
        productId: this.dataset.productId,
        sourceId: this.dataset.blockId,
        lineCount: items.length,
        requestedQuantity: totalQuantity,
        cartItemCount: Array.isArray(ajaxCart?.items) ? ajaxCart.items.length : undefined,
        httpStatus: response.status,
      });

      return ajaxCart;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        logStorefrontEvent('add-to-cart', 'Variant-grid add request was cancelled.', {
          operation: properties ? 'add-design-lines' : 'add-standard-lines',
          endpoint,
          productId: this.dataset.productId,
          sourceId: this.dataset.blockId,
        });
        deferredEventPromise.reject(error);
        throw error;
      }

      logStorefrontError('add-to-cart', 'Variant-grid add request failed.', error, {
        operation: properties ? 'add-design-lines' : 'add-standard-lines',
        productId: this.dataset.productId,
        sourceId: this.dataset.blockId,
        lineCount: items.length,
        requestedQuantity: totalQuantity,
        variantIds: items.map((item) => item.id),
        ...responseDiagnosticDetails(requestResponse, responsePayload, endpoint),
      });

      const errorMessage = error instanceof Error ? error.message : this.dataset.errorLabel || '';

      this.dispatchEvent(
        new CartErrorEvent({
          error: errorMessage,
          code: 'INVALID',
          detail: error instanceof Error && 'payload' in error ? error.payload : undefined,
        })
      );

      try {
        const ajaxCart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: {
            didError: true,
            items: ajaxCart.items,
            source: 'variant-quantity-grid',
            sourceId: this.dataset.blockId || '',
            itemCount: totalQuantity,
            productId: this.dataset.productId || '',
          },
        });
      } catch (cartError) {
        if (cartError?.name !== 'AbortError') {
          logStorefrontError('cart', 'Cart recovery fetch failed after an add error.', cartError, {
            operation: 'refresh-after-variant-grid-add-error',
            productId: this.dataset.productId,
            sourceId: this.dataset.blockId,
          });
        }
        deferredEventPromise.reject(cartError);
      }

      throw error;
    }
  }

  #isDesignConflict(error) {
    return (
      error instanceof Error &&
      'code' in error &&
      error.code === CUSTOMER_DESIGN_CART_ERROR_CODES.DESIGN_CONFLICT
    );
  }

  async #resolveDesignConflict(error) {
    const builder = document.getElementById('customer-design-builder');
    if (
      !(builder instanceof HTMLElement) ||
      !('resolveCartDesignConflict' in builder) ||
      typeof builder.resolveCartDesignConflict !== 'function'
    ) {
      throw error;
    }

    const details = error instanceof Error && 'details' in error ? error.details : null;
    const currentDesign = details && typeof details === 'object' && 'current' in details ? details.current : null;
    const candidateDesign =
      details && typeof details === 'object' && 'candidate' in details
        ? details.candidate
        : this.#selectedDesign;

    return builder.resolveCartDesignConflict({ currentDesign, candidateDesign });
  }

  #currentConflictApplication(error) {
    const details = error instanceof Error && 'details' in error ? error.details : null;
    if (!details || typeof details !== 'object' || !('current' in details)) return null;
    return details.current && typeof details.current === 'object' ? details.current : null;
  }

  #openBuilderDialog(builder) {
    const refDialog =
      builder && 'refs' in builder && builder.refs?.dialog instanceof HTMLDialogElement
        ? builder.refs.dialog
        : null;
    const dialog = refDialog || builder?.querySelector?.('dialog[open]');
    return dialog instanceof HTMLDialogElement && dialog.open ? dialog : null;
  }

  #completeRecoveredDesignAdd(result, submissionInputs) {
    if (!result || typeof result !== 'object') return;
    const ajaxCart = 'cart' in result ? result.cart : result;
    if (Array.isArray(ajaxCart?.items)) this.#syncCartConstraints(ajaxCart.items);
    for (const input of submissionInputs) input.value = '0';
    const stillShowingSubmission = submissionInputs.some(
      (input) => input.isConnected && this.contains(input)
    );
    if (!stillShowingSubmission) return;
    this.#setState('success', this.dataset.addedLabel || '');
    if (this.#stateTimer) window.clearTimeout(this.#stateTimer);
    this.#stateTimer = window.setTimeout(() => this.#setState('idle'), 1500);
    this.#syncControls();
  }

  async #replaceDesignItems(
    items,
    selection,
    options,
    expectedCurrentApplication,
    builder,
    recovered = false,
    submissionInputs = []
  ) {
    try {
      const builderDialog = this.#openBuilderDialog(builder);
      const result = await addCustomerDesignItems(items, selection, {
        ...options,
        sourceElement: builderDialog || options.sourceElement,
        context: builderDialog ? 'dialog' : options.context,
        replaceExisting: true,
        expectedCurrentApplication,
      });
      await builder?.completeCartDesignConflict?.();
      if (recovered) this.#completeRecoveredDesignAdd(result, submissionInputs);
      return result;
    } catch (replacementError) {
      if (this.#isDesignConflict(replacementError)) {
        const decision = await this.#resolveDesignConflict(replacementError);
        if (decision !== 'replace') return null;
        const nextExpectedApplication = this.#currentConflictApplication(replacementError);
        if (!nextExpectedApplication) throw replacementError;
        return this.#replaceDesignItems(
          items,
          selection,
          options,
          nextExpectedApplication,
          builder,
          recovered,
          submissionInputs
        );
      }

      if (replacementError?.name !== 'AbortError') {
        logStorefrontError('add-to-cart', 'Replacing the cart design failed.', replacementError, {
          operation: 'replace-cart-design',
          productId: this.dataset.productId,
          sourceId: this.dataset.blockId,
          lineCount: items.length,
        });
      }

      const retry = () =>
        this.#replaceDesignItems(
          items,
          selection,
          options,
          expectedCurrentApplication,
          builder,
          true,
          submissionInputs
        );
      await builder?.completeCartDesignConflict?.(replacementError, retry);
      throw replacementError;
    }
  }

  async #addDesignItems(items, submissionInputs, totalQuantity) {
    if (!this.#selectedDesign) throw new Error(this.dataset.errorLabel || '');
    const selection = this.#selectedDesign;
    const builder = document.getElementById('customer-design-builder');
    const builderDialog = this.#openBuilderDialog(builder);
    const popupTriggered = Boolean(this.#designCompletion && builderDialog);
    const options = {
      source: 'variant-quantity-grid',
      sourceId: this.dataset.blockId || '',
      signal: this.#requestController?.signal,
      sourceElement: popupTriggered ? builderDialog : this,
      context: popupTriggered ? 'dialog' : 'product',
    };

    try {
      const prepared = await prepareCustomerDesignAdd(items, selection, options);
      if (prepared.mode === 'native') {
        const cartItems = prepared.parts.map((part) => ({
          id: part.variantId,
          quantity: part.quantity,
          properties: part.properties,
          sellingPlanId: part.sellingPlanId,
        }));
        return this.#addStandardItems(cartItems, totalQuantity);
      }
      return await addCustomerDesignItems(items, selection, options);
    } catch (error) {
      if (!this.#isDesignConflict(error)) {
        if (error?.name !== 'AbortError') {
          logStorefrontError('add-to-cart', 'Preparing customer-design lines failed.', error, {
            operation: 'prepare-design-add',
            productId: this.dataset.productId,
            sourceId: this.dataset.blockId,
            lineCount: items.length,
            requestedQuantity: totalQuantity,
          });
        }
        throw error;
      }

      logStorefrontError('add-to-cart', 'A different customer design is already in the cart.', error, {
        operation: 'resolve-design-conflict',
        productId: this.dataset.productId,
        sourceId: this.dataset.blockId,
      });

      const decision = await this.#resolveDesignConflict(error);
      if (decision !== 'replace') return null;
      const expectedCurrentApplication = this.#currentConflictApplication(error);
      if (!expectedCurrentApplication) throw error;
      return this.#replaceDesignItems(
        items,
        selection,
        options,
        expectedCurrentApplication,
        builder,
        false,
        submissionInputs
      );
    }
  }

  // Constraint validation is the only browser-native tooltip that opens on demand; the
  // submit button's title waits for a hover that never happens on touch. #syncControls
  // clears the message again on the next quantity change.
  #reportQuantityRequired() {
    const message = this.dataset.selectQuantityHint || '';
    const input = this.#quantityInputs.find((item) => !item.disabled);
    if (!message || !input) return;

    input.setCustomValidity(message);
    input.reportValidity();
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-quantity-grid-form]')) return;

    event.preventDefault();
    if (this.#isSubmitting) {
      this.#settleDesignCompletion(
        undefined,
        new Error(this.dataset.errorLabel || 'customer_design_add_in_progress')
      );
      return;
    }

    if (this.#isCustomizable) this.#hydrateSelectedDesign();

    if (!this.hasSelectedQuantity()) {
      this.#syncControls();
      this.#settleDesignCompletion(
        undefined,
        new Error(this.dataset.errorLabel || 'customer_design_add_unavailable')
      );
      if (this.#showCustomizeCta) this.#openCustomerDesignBuilder(event, { addToCartOnSave: false });
      else this.#reportQuantityRequired();
      return;
    }

    if (this.#needsDesign) {
      this.#openCustomerDesignBuilder(event);
      this.#settleDesignCompletion(
        undefined,
        new Error(this.dataset.errorLabel || 'customer_design_selection_required')
      );
      return;
    }

    const submissionInputs = this.#quantityInputs;
    const items = submissionInputs
      .map((input) => ({
        id: input.dataset.variantId || '',
        quantity: this.#normalizeQuantity(input),
        unitPriceCents: Number.parseInt(input.dataset.price || '0', 10) || 0,
        disabled: input.disabled,
      }))
      .filter((item) => !item.disabled && item.id && item.quantity > 0);

    if (items.length === 0) {
      this.#syncControls();
      this.#settleDesignCompletion(
        undefined,
        new Error(this.dataset.errorLabel || 'customer_design_add_unavailable')
      );
      return;
    }

    this.#isSubmitting = true;
    this.#setState('loading');
    this.#requestController?.abort();
    this.#requestController = new AbortController();

    const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);

    try {
      const result = this.#isCustomizable
        ? await this.#addDesignItems(
            items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
              unitPriceCents: item.unitPriceCents,
              properties: { ...this.#customerDesignProperties },
            })),
            submissionInputs,
            totalQuantity
          )
        : await this.#addStandardItems(items, totalQuantity);

      if (!result) {
        this.#setState('idle');
        this.#settleDesignCompletion(null);
        return;
      }

      const ajaxCart = 'cart' in result ? result.cart : result;
      if (Array.isArray(ajaxCart?.items)) this.#syncCartConstraints(ajaxCart.items);

      for (const input of submissionInputs) input.value = '0';
      this.#setState('success', this.dataset.addedLabel || '');

      if (this.#stateTimer) window.clearTimeout(this.#stateTimer);
      this.#stateTimer = window.setTimeout(() => this.#setState('idle'), 1500);
      this.#settleDesignCompletion(result);
    } catch (error) {
      this.#settleDesignCompletion(undefined, error);
      if (error instanceof DOMException && error.name === 'AbortError') {
        logStorefrontEvent('add-to-cart', 'Quantity-grid submission was cancelled.', {
          operation: this.#isCustomizable ? 'submit-design-add' : 'submit-standard-add',
          productId: this.dataset.productId,
          sourceId: this.dataset.blockId,
        });
        return;
      }

      logStorefrontError('add-to-cart', 'Quantity-grid submission failed.', error, {
        operation: this.#isCustomizable ? 'submit-design-add' : 'submit-standard-add',
        productId: this.dataset.productId,
        sourceId: this.dataset.blockId,
        lineCount: items.length,
        requestedQuantity: totalQuantity,
      });

      const errorMessage = this.#customerErrorMessage(error);
      this.#setState('error', errorMessage);
    } finally {
      this.#isSubmitting = false;
      this.#syncControls();
      this.#flushPendingProductContentMorph();
    }
  };

  #flushPendingProductContentMorph() {
    const morph = this.#pendingProductContentMorph;
    this.#pendingProductContentMorph = null;
    if (!morph) return;

    try {
      morph();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        logStorefrontError('add-to-cart', 'Deferred product content update failed.', error, {
          operation: 'morph-product-content',
          productId: this.dataset.productId,
          sourceId: this.dataset.blockId,
        });
      }
    }
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) return;

        const morph = () => {
          const nextGrid = Array.from(detail.html.querySelectorAll('variant-quantity-grid')).find(
            (grid) => grid instanceof HTMLElement && grid.dataset.blockId === this.dataset.blockId
          );
          const nextContent = nextGrid?.querySelector('[data-quantity-grid-content]');
          const currentContent = this.querySelector('[data-quantity-grid-content]');

          if (!nextGrid || !nextContent || !currentContent) return;

          const activeElement = document.activeElement;
          const focusIdentity =
            activeElement instanceof HTMLElement && currentContent.contains(activeElement)
              ? {
                  id: activeElement.id,
                  inputId: activeElement.dataset.inputId,
                  optionValueId: activeElement.dataset.optionValueId,
                }
              : null;

          currentContent.replaceWith(nextContent.cloneNode(true));

          if (focusIdentity) {
            const focusableControls = Array.from(this.querySelectorAll('input:not([disabled]), select:not([disabled])'));
            const exactFocusTarget = focusableControls.find((element) => {
              if (!(element instanceof HTMLElement)) return false;
              if (focusIdentity.id && element.id === focusIdentity.id) return true;
              return (
                focusIdentity.inputId &&
                element.dataset.inputId === focusIdentity.inputId &&
                element.dataset.optionValueId === focusIdentity.optionValueId
              );
            });
            const samePositionTarget = focusableControls.find(
              (element) => element instanceof HTMLElement &&
                focusIdentity.inputId &&
                element.dataset.inputId === focusIdentity.inputId
            );
            const checkedTarget = focusableControls.find(
              (element) => element instanceof HTMLInputElement && element.checked
            );
            const focusTarget = exactFocusTarget ?? samePositionTarget ?? checkedTarget ?? focusableControls[0];

            focusTarget?.focus({ preventScroll: true });
          }

          if (nextGrid instanceof HTMLElement) {
            if (nextGrid.dataset.productId) {
              this.dataset.productId = nextGrid.dataset.productId;
            }
            this.dataset.customizable = nextGrid.dataset.customizable || 'false';
            this.dataset.cartProductQuantity = nextGrid.dataset.cartProductQuantity || '0';
            this.dataset.quantityDiscountTiers = nextGrid.dataset.quantityDiscountTiers || '[]';
            this.dataset.moneyFormat = nextGrid.dataset.moneyFormat || this.dataset.moneyFormat || '';
            this.dataset.currency = nextGrid.dataset.currency || this.dataset.currency || '';
          }

          this.#hydrateSelectedDesign();
        };

        if (this.#isSubmitting) {
          this.#pendingProductContentMorph = morph;
          return;
        }

        morph();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          logStorefrontError('add-to-cart', 'Product update event failed.', error, {
            operation: 'sync-product-selection',
            productId: this.dataset.productId,
            sourceId: this.dataset.blockId,
          });
        }
      });
  };
}

if (!customElements.get('variant-quantity-grid')) {
  customElements.define('variant-quantity-grid', VariantQuantityGrid);
}
